// src/constants/apiEndpoints.js
//
// All backend API endpoint paths, centralized here instead of scattered
// across .env as VUE_APP_API_* variables. The only thing that should still
// live in .env is what's genuinely environment-specific: the API host
// (VUE_APP_API_URL / VUE_APP_BASE_URL), secrets (VUE_APP_KEY), and a couple
// of non-URL config values (VUE_APP_SHELF_LIST/REPEAT). Endpoint paths never
// change between dev/staging/production, so they don't belong in env config
// - they belong in code, where they're easy to find, grep, and refactor.
//
// Usage:
//   import apiEndpoints from '@/constants/apiEndpoints'
//   axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_USER}`)

const apiEndpoints = Object.freeze({
  API_AUDITOR: 'auditor/',
  API_LOGIN_URL: 'auth/token',
  API_RESET_PASSWORD_URL: 'users/reset_password',
  API_CHECK_TOKEN: 'auth/check_token',
  API_GET_CURRENCIES: 'common/currencies',
  API_GET_SETTING: 'settings/list',
  API_GET_SETTING_COMMON: 'common/settings/',
  API_GET_SINGLE_SETTING: 'common/settings/single/',
  API_UPDATE_SETTING: 'settings/update',
  API_GET_USER: 'users/list',
  API_ADD_USER: 'users/new',
  API_CHECK_USERNAME: 'users/check_username',
  API_EDIT_USER: 'users/edit/',
  API_EDIT_PASSWORD_USER: 'users/edit_password',
  API_UPDATE_USER: 'users/update',
  API_DELETE_USER: 'users/delete',
  API_GET_ROLES: 'roles/list',
  API_GET_CONTACTS: 'contacts/list',
  API_GET_CONTACT_DETAILS: 'contacts/',
  API_CREATE_CONTACT: 'contacts/create',
  API_EDIT_CONTACT: 'contacts/edit',
  API_DELETE_CONTACT: 'contacts/delete/',
  API_IMPORT_CONTACTS: 'contacts/import',
  API_GET_BRANCH: 'branches/list',
  API_GET_BRANCHES: 'branches/list',
  API_ADD_BRANCH: 'branches/create',
  API_EDIT_BRANCH: 'branches/edit/',
  API_UPDATE_BRANCH: 'branches/update',
  API_DELETE_BRANCH: 'branches/delete',
  API_GET_BRANCH_STATS: 'branches/stats',
  API_TOGGLE_BRANCH_STATUS: 'branches/toggle-status',
  API_GET_REGION: 'regions/list',
  API_GET_REGION_WITH_USERS: 'regions/with-users',
  API_GET_REGIONS_WITH_BRANCHES: 'region-branch/list',
  API_ADD_REGION: 'regions/create',
  API_EDIT_REGION: 'regions/edit/',
  API_UPDATE_REGION: 'regions/up',
  API_DELETE_REGION: 'regions/delete',
  API_GET_DEPARTEMENTS: 'departements/list',
  API_ADD_DEPARTEMENTS: 'departements/create',
  API_EDIT_DEPARTEMENTS: 'departements/edit/',
  API_UPDATE_DEPARTEMENTS: 'departements/update',
  API_DELETE_DEPARTEMENTS: 'departements/delete',
  API_TICKETS: 'tickets',
  API_GET_ACTIVITY_LOG: 'common/activity-log',
  API_GET_UNIT: 'units/list',
  API_CREATE_UNIT: 'units/create',
  API_EDIT_UNIT: 'units/edit',
  API_DELETE_UNIT: 'units/delete',
  API_GET_ITEMS: 'items/list',
  API_CREATE_ITEMS: 'items/create',
  API_EDIT_ITEMS: 'items/edit',
  API_DELETE_ITEMS: 'items/delete',
  API_IMPORT_ITEMS: 'items/import',
  API_EXPORT_ITEMS: 'items/export',
  API_ADD_ITEMS: 'users/addbranch',
  API_GET_ITEM_LOOKUP: 'transactions/purchase_order/item_references/',
  API_CREATE_ITEM_LOOKUP: 'transactions/purchase_order/item_references/create',
  API_GET_STOCKS: 'transactions/stocks/list',
  API_GET_STOCK_DETAILS: 'transactions/stocks/list/',
  API_CREATE_STOCKS: 'transactions/stocks/create',
  API_EXPORT_STOCKS: 'transactions/stocks/export',
  API_GET_ITEM_STOCKS: 'item_stocks/list/',
  API_GET_ITEM_STOCKS_HISTORY: 'items/stocks/',
  API_GET_ITEM_STOCKS_POSITION: 'item_stocks/position/',
  API_GET_ITEM_STOCKS_SHELVES: 'item_stocks/shelves/',
  API_GET_ITEM_CURRENT_STOCK: 'item_stocks/stock/',
  API_CREATE_ITEM_STOCK: 'item_stocks/create/',
  API_EDIT_ITEM_STOCK: 'item_stocks/edit/',
  API_DELETE_ITEM_STOCK: 'item_stocks/delete/',
  API_EXPORT_ITEM_STOCKS: 'item_stocks/export',
  API_GET_MIN_STOCK_ITEM: 'item_min_stocks/',
  API_CREATE_MIN_STOCK_ITEM: 'item_min_stocks/create',
  API_UPDATE_MIN_STOCK_ITEM: 'item_min_stocks/',
  API_GET_ASSET: 'assets/list',
  API_GET_ASSET_ITEMS: 'assets/items',
  API_GET_SINGLE_ASSET: 'assets/single/',
  API_CREATE_ASSET: 'assets/create',
  API_EDIT_ASSET: 'assets/edit',
  API_DELETE_ASSET: 'assets/delete/',
  API_DOWNLOAD_ASSET: 'assets/download',
  API_EXPORT_ASSET: 'assets/export',
  API_TRANSFER_ASSET: 'assets/transfer',
  API_GET_ASSET_DOCUMENTS: 'assets/documents',
  API_UPDATE_ASSET: 'assets/update',
  API_CONFIRM_UPDATE_ASSET: 'assets/update/confirm/',
  API_DISPOSE_ASSET: 'assets/dispose',
  API_CONFIRM_DISPOSE_ASSET: 'assets/dispose/confirm/',
  API_GET_ITEM_DISPOSALS: 'transactions/item_disposals/list',
  API_GET_ITEM_DISPOSALS_SINGLE: 'transactions/item_disposals/',
  API_CREATE_ITEM_DISPOSALS: 'transactions/item_disposals/create',
  API_EDIT_ITEM_DISPOSALS: 'transactions/item_disposals/edit',
  API_DETAIL_ITEM_DISPOSALS: 'transactions/item_disposals/detail/',
  API_CONFIRM_ITEM_DISPOSALS: 'transactions/item_disposals/confirm/',
  API_DELETE_ITEM_DISPOSALS: 'transactions/item_disposals/delete/',
  API_DOWNLOAD_ITEM_DISPOSALS: 'transactions/item_disposals/download/',
  API_GET_DASHBOARD: 'dashboard/data',
  API_GET_DOCUMENTS: 'documents/',
  API_CREATE_DOCUMENT: 'documents/create',
  API_DELETE_DOCUMENT: 'documents/delete/',
  API_GET_MATERIAL_REQUEST: 'transactions/material_requests/list',
  API_GET_MATERIAL_REQUEST_DETAILS: 'transactions/material_requests/detail/',
  API_CREATE_MATERIAL_REQUEST: 'transactions/material_requests/create',
  API_EDIT_MATERIAL_REQUEST: 'transactions/material_requests/edit',
  API_ACCEPT_MATERIAL_REQUEST: 'transactions/material_requests/accept',
  API_SEND_MATERIAL_REQUEST: 'transactions/material_requests/send',
  API_CONFIRM_RECEIVE_MATERIAL_REQUEST: 'transactions/material_requests/confirm_receive',
  API_DELETE_MATERIAL_REQUEST: 'transactions/material_requests/delete/',
  API_DOWNLOAD_MATERIAL_REQUEST: 'transactions/material_requests/download/',
  API_EXPORT_MATERIAL_REQUEST: 'transactions/material_requests/export',
  API_CLOSE_MATERIAL_REQUEST: 'transactions/material_requests/close',
  API_RECEIVE_MATERIAL_REQUEST: 'transactions/material_requests/receive',
  API_GET_MATERIAL_REQUEST_RECEIVES: 'transactions/material_requests/receive/list',
  API_GET_MATERIAL_REQUEST_RECEIVE_DETAILS: 'transactions/material_requests/receive/details/',
  API_GET_MATERIAL_REQUEST_VALIDATION: 'transactions/material_requests/validation',
  API_GET_MATERIAL_REQUEST_UNSENT: 'transactions/material_requests/unsent/',
  API_MATERIAL_REQUEST_DELIVERY_CONFIRMATION: 'transactions/material_requests/delivery_confirmation/',
  API_GET_MATERIAL_REQUEST_CHATS: 'transactions/material_requests/chats/',
  API_CREATE_MATERIAL_REQUEST_CHATS: 'transactions/material_requests/chats/create',
  API_GET_PURCHASE_REQUESTS: 'transactions/purchase_requests/list',
  API_GET_PURCHASE_REQUESTS_DETAILS: 'transactions/purchase_requests/detail/',
  API_GET_PURCHASE_REQUEST_ITEM_STATUS_LIST: 'transactions/purchase_requests/item_status/list',
  API_EXPORT_PURCHASE_REQUEST_ITEM_STATUS_LIST: 'transactions/purchase_requests/item_status/export',
  API_GET_PURCHASE_REQUESTS_SINGLE: 'transactions/purchase_requests/',
  API_CREATE_PURCHASE_REQUEST: 'transactions/purchase_requests/create',
  API_EDIT_PURCHASE_REQUEST: 'transactions/purchase_requests/edit',
  API_DOWNLOAD_PURCHASE_REQUEST: 'transactions/purchase_requests/download/',
  API_EXPORT_PURCHASE_REQUEST: 'transactions/purchase_requests/export',
  API_CLOSE_PURCHASE_REQUEST: 'transactions/purchase_requests/close',
  API_CONFIRM_PURCHASEL_REQUEST: 'transactions/purchase_requests/confirm/',
  API_CONFIRM_PURCHASE_REQUEST_CENTER: 'transactions/purchase_requests/confirm/center',
  API_REVISE_PURCHASE_REQUEST: 'transactions/purchase_requests/revise',
  API_GET_PURCHASE_REQUESTS_REVISES: 'transactions/purchase_requests/revises',
  API_PURCHASE_REQUEST_CHATS: 'transactions/purchase_requests/chats',
  API_GET_PURCHASE_REQUESTS_DOCUMENTS: 'transactions/purchase_request/documents',
  API_CREATE_PURCHASE_REQUEST_DIRECT: 'transactions/purchase_requests_direct/create',
  API_GET_PR_PLAN: 'transactions/purchase_requests/pr_plan/list',
  API_DELETE_PR_PLAN: 'transactions/purchase_requests/pr_plan/delete/',
  API_GET_PURCHASE_ORDER: 'transactions/purchase_order/list',
  API_GET_PURCHASE_ORDER_SINGLE: 'transactions/purchase_order/',
  API_GET_PURCHASE_ORDER_DETAILS: 'transactions/purchase_order/detail/',
  API_CREATE_PURCHASE_ORDER: 'transactions/purchase_order/create',
  API_EDIT_PURCHASE_ORDER: 'transactions/purchase_order/edit',
  API_DELETE_PURCHASE_ORDER: 'transactions/purchase_order/delete/',
  API_PAY_PURCHASE_ORDER: 'transactions/purchase_order/pay',
  API_CONFIRM_PURCHASE_ORDER: 'transactions/purchase_order/confirm',
  API_CONFIRM_PURCHASE_ORDER_CENTER: 'transactions/purchase_order/confirm/center',
  API_DOWNLOAD_PURCHASE_ORDER: 'transactions/purchase_order/download/',
  API_EXPORT_PURCHASE_ORDER: 'transactions/purchase_order/export',
  API_PURCHASE_ORDER_CHATS: 'transactions/purchase_order/chats',
  API_PURCHASE_ORDER_SEND_REMARK: 'transactions/purchase_order/remark',
  API_REQUEST_EARLY_FINISH_PURCHASE_ORDER: 'transactions/purchase_order/early_finish',
  API_CONFIRM_EARLY_FINISH_PURCHASE_ORDER: 'transactions/purchase_order/early_finish/confirm',
  API_GET_PURCHASE_MANAGEMENT: 'transactions/purchase_management',
  API_RECEIVE_PURCHASING_PURCHASE_ORDER: 'transactions/purchase_order/receive/purchasing/',
  API_GET_PURCHASE_ORDER_RECEIVES_PURCHASING: 'transactions/purchase_order/receive/purchasing/data',
  API_GET_PURCHASE_ORDER_RECEIVE_PURCHASING_DETAILS: 'transactions/purchase_order/receive/purchasing/detail',
  API_GET_PURCHASE_ORDER_RECEIVE_PURCHASING_DOWNLOAD: 'transactions/purchase_order/receive/purchasing/download',
  API_RECEIVE_WAREHOUSE_PURCHASE_ORDER: 'transactions/purchase_order/receive/warehouse/',
  API_CONFIRM_PURCHASE_ORDER_RECEIVE_WAREHOUSE: 'transactions/purchase_order/confirm/warehouse/',
  API_GET_PURCHASE_ORDER_RECEIVES_WAREHOUSE: 'transactions/purchase_order/receive/warehouse/data',
  API_GET_PURCHASE_ORDER_RECEIVES_WAREHOUSE_ONGOING: 'transactions/purchase_order/receive/warehouse/ongoing',
  API_GET_PURCHASE_ORDER_RECEIVES_WAREHOUSE_FINISHED: 'transactions/purchase_order/receive/warehouse/finished',
  API_GET_PURCHASE_ORDER_RECEIVE_WAREHOUSE_DETAILS: 'transactions/purchase_order/receive/warehouse/detail',
  API_GET_PURCHASE_ORDER_RECEIVE_WAREHOUSE_DOWNLOAD: 'transactions/purchase_order/receive/warehouse/download',
  API_GET_PURCHASE_ORDER_RECEIVE_WAREHOUSE_CHECK: 'transactions/purchase_order/receive/warehouse/check/',
  API_RECEIVE_ASSET_PURCHASE_ORDER: 'transactions/purchase_order/receive/assets/',
  API_CONFIRM_ASSET_PURCHASE_ORDER: 'transactions/purchase_order/confirm/assets/',
  API_GET_ASSET_CHECK: 'transactions/purchase_order/get/assets/',
  API_GET_RECEIVE_ASSET: 'transactions/purchase_order/receive/warehouse/asset',
  API_GET_PURCHASE_ORDER_RECEIVE_ASSET_CHECK: 'transactions/purchase_order/receive/assets/check/',
  API_GET_ITEM_ASSET_HISTORY: 'transactions/purchase_order/assets/history/',
  API_GET_PURCHASE_ORDER_SUMMARY: 'transactions/purchase_order/summary/get',
  API_GET_PO_SUMMARY_CHART: 'transactions/purchase_order/summary/chart',
  API_GET_PO_SUMMARY_PIE: 'transactions/purchase_order/summary/pie',
  API_GET_PAYMENT_REQUESTS: 'transactions/payment_requests/list',
  API_GET_PAYMENT_REQUEST_SINGLE: 'transactions/payment_requests/',
  API_GET_DETAIL_PAYMENT_REQUEST: 'transactions/payment_requests/details/',
  API_CREATE_PAYMENT_REQUEST: 'transactions/payment_requests/create',
  API_EDIT_PAYMENT_REQUEST: 'transactions/payment_requests/edit',
  API_CONFIRMA_PAYMENT_REQUEST: 'transactions/payment_requests/confirm',
  API_DOWNLOAD_PAYMENT_REQUEST: 'transactions/payment_requests/download/',
  API_REVISE_PAYMENT_REQUEST: 'transactions/payment_requests/revise',
  API_GET_PAYMENT_REQUESTS_REVISES: 'transactions/payment_requests/revises',
  API_VOID_PAYMENT_REQUEST: 'transactions/payment_requests/void',
  API_PAYMENT_REQUEST_CHATS: 'transactions/payment_requests/chat',
  API_PAYMENT_REQUEST_REJECT: 'transactions/payment_requests/reject',
  API_GET_RETURNS: 'transactions/returns/list',
  API_GET_RETURN_DETAILS: 'transactions/returns/list/',
  API_GET_TRANSFERS: 'transactions/transfers/list',
  API_GET_TRANSFER_DETAILS: 'transactions/transfers/list/',
  CREATE_TRANSFER: 'transactions/transfers/create',
  DOWNLOAD_TRANSFER: 'transactions/transfers/download/',
  API_RECEIVE_TRANSFER: 'transactions/transfers/receive/',
  API_DECLINE_TRANSFER: 'transactions/transfers/decline/',
  API_REJECT_TRANSFER: 'transactions/transfers/reject/',
  API_CONFIRM_TRANSFER_BY_LAB_RECEIVER: 'transactions/transfers/confirm/',
  API_CONFIRM_TRANSFER_BY_LAB_SENDER: 'transactions/transfers/confirm/lab/sender',
  API_CONFIRM_TRANSFER_BY_WAREHOUSE_SENDER: 'transactions/transfers/confirm/warehouse/sender',
  API_CONFIRM_TRANSFER_BY_WAREHOUSE_RECEIVER: 'transactions/transfers/confirm/warehouse/receiver',
  API_GET_STOCK_OPNAMES: 'transactions/stock_opname/list',
  API_GET_STOCK_OPNAME_DETAILS: 'transactions/stock_opname/detail/',
  CREATE_STOCK_OPNAME: 'transactions/stock_opname/create',
  API_CONFIRM_STOCK_OPNAME_BY_FINANCE: 'transactions/stock_opname/confirm/finance/',
  API_CONFIRM_STOCK_OPNAME_BY_LAB: 'transactions/stock_opname/confirm/lab/',
  API_CONFIRM_STOCK_OPNAME_BY_ADMINISTRATOR: 'transactions/stock_opname/confirm/administrator/',
  API_GET_PAYMENT_REQUEST: 'transactions/payment_requests/list',

  // In-app notifications (navbar bell) - see app/routes/common.js on the backend
  API_GET_NOTIFICATIONS: 'common/notifications',
  API_GET_NOTIFICATIONS_UNREAD_COUNT: 'common/notifications/unread-count',
  API_MARK_ALL_NOTIFICATIONS_READ: 'common/notifications/read-all',
  API_MARK_ALL_NOTIFICATIONS_UNREAD: 'common/notifications/unread-all',
  API_MARK_NOTIFICATION_READ: 'common/notifications/', // + `${id}/read`
  API_MARK_NOTIFICATION_UNREAD: 'common/notifications/', // + `${id}/unread`
  API_ARCHIVE_NOTIFICATION: 'common/notifications/', // + `${id}/archive`
  API_UNARCHIVE_NOTIFICATION: 'common/notifications/', // + `${id}/unarchive`
  API_DELETE_NOTIFICATION: 'common/notifications/', // + `${id}`
})

export default apiEndpoints
